import React, { useCallback, useEffect, useState } from 'react';
import { Div, Flex, TextArea } from '@gaiads/telia-react-component-library';

import { HTMLElementProps } from '../../../utils/types';
import { BaseEditableItemFieldProps } from '../EditableItem';
import EditableItemIcons, { EditableItemIconsProps } from '../EditableItemIcons/EditableItemIcons';

export interface EditableItemTextAreaProps extends Omit<BaseEditableItemFieldProps, 'showSave'>, EditableItemIconsProps {
  value?: string;
  edit?: boolean;
  onSave?: (value?: string) => Promise<boolean> | boolean;
  onBlur?: () => void;
  field?: (content?: string) => JSX.Element;
  textAreaProps?: Partial<React.ComponentProps<typeof TextArea> & HTMLElementProps>;
  flexProps?: Partial<React.ComponentProps<typeof Flex> & HTMLElementProps>;
  divProps?: Partial<React.ComponentProps<typeof Div> & HTMLElementProps>;
  iconFlexProps?: Partial<React.ComponentProps<typeof Flex> & HTMLElementProps>;
}

export default function EditableItemInput(props: EditableItemTextAreaProps) {
  const {
    edit,
    setIsEditingItem,
    onSave,
    onBlur,
    onDelete,
    field,
    isReference,
    showDelete,
    flexProps,
    divProps,
    textAreaProps,
    iconFlexProps,
    editIconProps,
    deleteIconProps,
  } = props;

  // TRCL TextArea component doesn't have ref prop like Input has inputRef
  //const inputRef = useRef<HTMLTextAreaElement>(null);

  const [value, setValue] = useState(props.value);
  const [isEditingInput, setIsEditingInput] = useState(false);

  /* blur conflicts with icon click until fixed in TRCL
  const handleBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    console.log(event);
    onBlur?.();
    handleEditing(false);
    setValue(textAreaProps?.value);
  };*/

  const handleEditing = useCallback(
    (isEditing: boolean) => {
      setIsEditingInput(isEditing);
      setIsEditingItem?.(isEditing);
    },
    [setIsEditingItem],
  );

  const handleSave = async () => {
    const success = await onSave?.(value);
    if (success) {
      handleEditing(false);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Escape') {
      onBlur?.();
      handleEditing(false);
      setValue(props.value);
    } else if (event.key === 'Enter') {
      handleSave();
    }
  };

  useEffect(() => {
    if (edit) {
      handleEditing(true);
    } else {
      handleEditing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [edit]);

  useEffect(() => {
    if (!isEditingInput && textAreaProps?.errorText) {
      handleEditing(true);
    }
  }, [textAreaProps?.errorText, isEditingInput, handleEditing]);

  /*useEffect(() => {
    if (isEditingInput && focusOnEdit) {
      inputRef.current?.focus();
    }
  }, [isEditingInput, focusOnEdit]);*/

  useEffect(() => {
    setValue(props.value);
  }, [props.value]);

  const valueOrPlaceholder = value || `${textAreaProps?.placeholder}${textAreaProps?.required ? ' ' : ''}`;

  return (
    <Flex
      {...flexProps}
      className={[
        'editable-field__field',
        !value && 'editable-field__field--blank',
        isReference && 'editable-field__field--reference',
        flexProps?.className,
      ]}
    >
      <Div {...divProps} className={['editable-field__input-container', divProps?.className]}>
        {isEditingInput ? (
          <TextArea
            //inputRef={inputRef}
            label=""
            className="editable-field__input"
            onChange={(event, value) => setValue(value)}
            onKeyDown={handleKeyPress}
            //onBlur={handleBlur}
            value={value}
            {...textAreaProps}
          />
        ) : (
          <>
            <span className="telia-react-element">{field?.(valueOrPlaceholder) || valueOrPlaceholder}</span>
            {textAreaProps?.required && !value && <span className="telia-react-element telia-react-input_required">*</span>}
          </>
        )}
      </Div>
      {!isEditingInput && (
        <EditableItemIcons
          showDelete={showDelete}
          onDelete={onDelete}
          setIsEditing={handleEditing}
          flexProps={iconFlexProps}
          editIconProps={editIconProps}
          deleteIconProps={deleteIconProps}
        />
      )}
    </Flex>
  );
}
